import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Timeline, Tag, Typography, Empty, Spin, Space } from "antd";
import {
  PlusCircleOutlined,
  EditOutlined,
  SwapOutlined,
  SendOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  UserSwitchOutlined,
  MessageOutlined,
  DeleteOutlined,
  HistoryOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { ApiService } from "../api";

const { Text } = Typography;

type AuditEntry = {
  id: number;
  action: string;
  action_display: string;
  actor_name: string;
  description: string;
  from_status: string | null;
  to_status: string | null;
  workflow_name: string | null;
  attempt: number | null;
  metadata: Record<string, any>;
  created_at: string;
};

type Props = {
  requestId?: string;
  visible: boolean;
  setVisible: (visible: boolean) => void;
};

// Icon + color per audit action, so the timeline is scannable at a glance.
// `color` is an antd Tag preset name; `hex` is the matching value for the icon.
const ACTION_META: Record<
  string,
  { color: string; hex: string; icon: JSX.Element }
> = {
  Created: { color: "green", hex: "#52c41a", icon: <PlusCircleOutlined /> },
  Updated: { color: "blue", hex: "#1677ff", icon: <EditOutlined /> },
  StatusChanged: { color: "geekblue", hex: "#2f54eb", icon: <SwapOutlined /> },
  SentToApproval: { color: "gold", hex: "#faad14", icon: <SendOutlined /> },
  WorkflowStarted: { color: "cyan", hex: "#13c2c2", icon: <PlayCircleOutlined /> },
  Approved: { color: "green", hex: "#52c41a", icon: <CheckCircleOutlined /> },
  Rejected: { color: "red", hex: "#ff4d4f", icon: <CloseCircleOutlined /> },
  Extended: { color: "purple", hex: "#722ed1", icon: <ClockCircleOutlined /> },
  OwnerChanged: { color: "volcano", hex: "#fa541c", icon: <UserSwitchOutlined /> },
  CommentAdded: { color: "blue", hex: "#1677ff", icon: <MessageOutlined /> },
  Deleted: { color: "red", hex: "#ff4d4f", icon: <DeleteOutlined /> },
};

const AuditTrailModal = (props: Props) => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!props.visible || !props.requestId) return;
    const load = async () => {
      setLoading(true);
      try {
        const data = await ApiService.apiMainRequestHistoryRetrieve(
          parseInt(props.requestId!)
        );
        setEntries((data as AuditEntry[]) ?? []);
      } catch (e) {
        setEntries([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [props.visible, props.requestId]);

  const items = entries.map((entry) => {
    const meta = ACTION_META[entry.action] ?? {
      color: "default",
      hex: "#8c8c8c",
      icon: <HistoryOutlined />,
    };
    const showTransition =
      (entry.from_status || entry.to_status) && entry.action !== "Created";

    return {
      color: meta.color,
      dot: <span style={{ fontSize: 16, color: meta.hex }}>{meta.icon}</span>,
      children: (
        <div style={{ paddingBottom: 4 }}>
          <Space size={8} align="center" wrap>
            <Tag color={meta.color} style={{ marginInlineEnd: 0 }}>
              {t(entry.action_display)}
            </Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {dayjs(entry.created_at).format("DD MMM YYYY · HH:mm")}
            </Text>
          </Space>

          {entry.description && (
            <div style={{ marginTop: 6, lineHeight: 1.5 }}>
              <Text>{entry.description}</Text>
            </div>
          )}

          {showTransition && (
            <div style={{ marginTop: 6 }}>
              <Tag>{entry.from_status ?? "—"}</Tag>
              <SwapOutlined style={{ color: "#bfbfbf", fontSize: 11 }} />
              <Tag color={meta.color} style={{ marginInlineStart: 8 }}>
                {entry.to_status ?? "—"}
              </Tag>
            </div>
          )}

          <div style={{ marginTop: 6 }}>
            <Space size={16} wrap>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <UserOutlined style={{ marginInlineEnd: 4 }} />
                {entry.actor_name}
              </Text>
              {entry.workflow_name && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t("Workflow")}: {entry.workflow_name}
                  {entry.attempt != null
                    ? ` · ${t("Attempt")} ${entry.attempt}`
                    : ""}
                </Text>
              )}
            </Space>
          </div>
        </div>
      ),
    };
  });

  return (
    <Modal
      title={
        <Space size={10} align="center">
          <HistoryOutlined style={{ fontSize: 18 }} />
          <span style={{ fontSize: 16, fontWeight: 600 }}>
            {t("Audit Trail")}
          </span>
          {!loading && entries.length > 0 && (
            <Tag style={{ marginInlineStart: 4 }}>{entries.length}</Tag>
          )}
        </Space>
      }
      open={props.visible}
      onCancel={() => props.setVisible(false)}
      footer={null}
      centered
      width={620}
      styles={{
        body: {
          paddingTop: 20,
          maxHeight: "62vh",
          overflowY: "auto",
        },
      }}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "48px 0" }}>
          <Spin />
        </div>
      ) : entries.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("No audit records found")}
          style={{ padding: "32px 0" }}
        />
      ) : (
        <Timeline items={items} style={{ paddingInlineStart: 4 }} />
      )}
    </Modal>
  );
};

export default AuditTrailModal;
